import {
    ColumnDefinition,
    ColumnDefinitionEnum,
    ColumnDefinitionEnumType,
    EventQueueWireDto,
    QueueColumnFilter,
    QueueFiltersMetadata,
    UnitQueueWireDto,
    WorkspaceQueueDto,
} from '@mark43/cad-api';
import { globalAttributes } from '../common/global-attributes';
import { WithColumnDefinition } from '../event-queues/get-event-filter-options';

// The backend ships `queueView.eventQueue` as an int. 1 = DISPATCH, 2 = TELEPHONE_RESPONSE_UNIT
export function filterEventQueueViewByEventQueueType(
    eventQueueType: WorkspaceQueueDto['eventQueue'],
    queueView: EventQueueWireDto
) {
    if (!eventQueueType || !queueView.queueFiltersMetadata.eventQueue) {
        return true;
    }
    if (eventQueueType === queueView.queueFiltersMetadata.eventQueue) {
        return true;
    }
    return false;
}

export function filterQueueViewsBySearchValue(
    searchValue: string,
    filterQuery: string | undefined
) {
    if (filterQuery) {
        const lowercaseQuery = filterQuery.toLocaleLowerCase();
        if (searchValue.indexOf(lowercaseQuery) === -1) {
            return false;
        }
    }
    return true;
}

export function getQueueColumnFiltersByType(
    queueColumnFilters: QueueColumnFilter[]
): Record<ColumnDefinition['name'], QueueColumnFilter[]> {
    return queueColumnFilters.reduce((acc, val) => {
        if (!acc[val.columnDefinition]) acc[val.columnDefinition] = [];
        acc[val.columnDefinition].push(val);
        return acc;
    }, {} as Record<ColumnDefinition['name'], QueueColumnFilter[]>);
}

export function filterQueueViewsByAssignmentStatus(
    queueView: EventQueueWireDto | UnitQueueWireDto,
    assignmentStatus: undefined | boolean
) {
    if (assignmentStatus === true && !queueView.queueFiltersMetadata.isAssigned) {
        return false;
    }
    if (assignmentStatus === false && queueView.queueFiltersMetadata.isAssigned) {
        return false;
    }
    return true;
}

export function filterQueueViewsByClass(
    queueView: EventQueueWireDto | UnitQueueWireDto,
    queueClasses: WorkspaceQueueDto['queueClasses']
) {
    if (
        queueClasses &&
        queueClasses.length > 0 &&
        !queueClasses.some(
            (queueClass) =>
                globalAttributes.agencyTypeGlobal[queueClass === 'MEDICAL' ? 'EMS' : queueClass] ===
                queueView.queueFiltersMetadata.agencyTypeGlobalAttrId
        )
    ) {
        return false;
    }
    return true;
}

export function filterEventQueueViewByDispatchArea(
    queueView: EventQueueWireDto,
    dispatchAreaIds: number[],
    allQueueColumnFilters: Record<string, QueueColumnFilter[]>
) {
    const hasEventDispatchAreaFilters = !!allQueueColumnFilters?.['DISPATCH_AREA']?.length;

    if (hasEventDispatchAreaFilters) {
        return filterQueueByDispatchAreaFilters(
            queueView.queueFiltersMetadata.columnDefinitionToFilters,
            allQueueColumnFilters
        );
    }

    return filterQueueByMonitoredDispatchAreas(
        queueView.queueFiltersMetadata.columnDefinitionToFilters,
        dispatchAreaIds
    );
}

function getDispatchAreaIdsForQueueColumnFilter(filters: QueueColumnFilter[]): Set<number> {
    return new Set(filters.map((f) => f.entityId));
}

export function filterUnitQueueViewByDispatchArea(
    queueView: UnitQueueWireDto,
    monitoredDispatchAreaIds: number[],
    allQueueColumnFilters: Record<string, QueueColumnFilter[]>
) {
    const hasUnitOrEventDispatchAreaFilters =
        allQueueColumnFilters['UNIT_DISPATCH_AREA']?.length ||
        allQueueColumnFilters['DISPATCH_AREA']?.length;

    // If either Unit or Event Dispatch areas are filtered, ignore Monitored dispatch areas.
    if (hasUnitOrEventDispatchAreaFilters) {
        return filterQueueByDispatchAreaFilters(
            queueView.queueFiltersMetadata.columnDefinitionToFilters,
            allQueueColumnFilters
        );
    }

    if (
        filterQueueByMonitoredDispatchAreas(
            queueView.queueFiltersMetadata.columnDefinitionToFilters,
            monitoredDispatchAreaIds
        )
    ) {
        return true;
    }

    if (queueView?.hasInProgressEmergency) {
        return monitoredDispatchAreaIds.includes(
            queueView.unitEmergencyLocationDispatchAreaId as number
        );
    }

    return false;
}

function filterQueueByDispatchAreaFilters(
    queueViewColumnDefinitionToFilters: QueueFiltersMetadata['columnDefinitionToFilters'],
    allQueueColumnFilters: Record<string, QueueColumnFilter[]>
) {
    const unitDispatchAreaIdsFromChosenFilters = getDispatchAreaIdsForQueueColumnFilter(
        allQueueColumnFilters['UNIT_DISPATCH_AREA'] ?? []
    );
    const eventDispatchAreaIdsFromChosenFilters = getDispatchAreaIdsForQueueColumnFilter(
        allQueueColumnFilters['DISPATCH_AREA'] ?? []
    );

    if (eventDispatchAreaIdsFromChosenFilters.size || unitDispatchAreaIdsFromChosenFilters.size) {
        const eventDispatchAreasForView =
            queueViewColumnDefinitionToFilters?.[ColumnDefinitionEnum.DISPATCH_AREA.name] ?? [];

        const unitDispatchAreasForView =
            queueViewColumnDefinitionToFilters?.[ColumnDefinitionEnum.UNIT_DISPATCH_AREA.name] ??
            [];

        return (
            eventDispatchAreasForView.some((id) => eventDispatchAreaIdsFromChosenFilters.has(id)) ||
            unitDispatchAreasForView.some((id) => unitDispatchAreaIdsFromChosenFilters.has(id))
        );
    }

    return true;
}

function filterQueueByMonitoredDispatchAreas(
    queueViewColumnDefinitionToFilters: QueueFiltersMetadata['columnDefinitionToFilters'],
    monitoredDispatchAreaIds: number[]
) {
    if (!monitoredDispatchAreaIds.length) return false;

    const dispatchAreas = [
        ...(queueViewColumnDefinitionToFilters?.[ColumnDefinitionEnum.DISPATCH_AREA.name] ?? []),
        ...(queueViewColumnDefinitionToFilters?.[ColumnDefinitionEnum.UNIT_DISPATCH_AREA.name] ??
            []),
    ];

    if (
        monitoredDispatchAreaIds.length > 0 &&
        !dispatchAreas.some((eventDispatchAreaId) => {
            return monitoredDispatchAreaIds.includes(eventDispatchAreaId);
        })
    )
        return false;

    return true;
}

export function filterQueueViewByQueueColumnFilters(
    queueFiltersMetadata: QueueFiltersMetadata,
    queueColumnFiltersByType: Record<ColumnDefinition['name'], QueueColumnFilter[]>
) {
    for (const columnDefinitionNameString in queueColumnFiltersByType) {
        const columnDefinitionName = columnDefinitionNameString as ColumnDefinitionEnumType;
        const queueColumnFilters = queueColumnFiltersByType[columnDefinitionName];
        if (
            !queueFiltersMetadata.columnDefinitionToFilters[columnDefinitionName] ||
            queueFiltersMetadata.columnDefinitionToFilters[columnDefinitionName].length === 0
        ) {
            return false;
        }
        const ret = queueColumnFilters
            .map((filter) => filter.entityId)
            .some((filterEntityId) => {
                return queueFiltersMetadata.columnDefinitionToFilters[
                    columnDefinitionName
                ].includes(filterEntityId);
            });
        if (ret === false) {
            return ret;
        }
    }
    return true;
}

export function entityIdsToFilterOptions<T extends { value: number; label: string }>(
    options: WithColumnDefinition<T>[],
    entityIds: number[],
    columnDefinition?: ColumnDefinitionEnumType
) {
    return options
        .filter((option) => entityIds.includes(option.value))
        .map((option) => ({
            value: option.value,
            label: option.label,
            columnDefinition: columnDefinition ?? option.columnDefinition,
        }));
}
